import { useState, useCallback, useId, type ReactNode } from 'react';
import {
  RadioButtonGroup,
  RadioButton,
  DatePicker,
  DatePickerInput,
  Button,
} from '@carbon/react';
import { format, isAfter } from 'date-fns';
import { parseDateString } from '../../utils/dates';
import styles from './dynamic-date-filter.module.scss';

interface DateFilterValue {
  type: string;
  start?: string;
  end?: string;
}

type DateFilterVariant = 'single-select' | 'multi-select';

interface BaseDynamicDateFilterProps {
  dateOptions: {
    value: string;
    label: string;
  }[];
  headingText?: string;
  variant?: DateFilterVariant;
}

interface SingleSelectProps extends BaseDynamicDateFilterProps {
  variant?: 'single-select';
  value?: DateFilterValue;
  onChange: (value?: DateFilterValue) => void;
}

interface MultiSelectProps extends BaseDynamicDateFilterProps {
  variant: 'multi-select';
  values?: DateFilterValue[];
  onChange: (values?: DateFilterValue[]) => void;
}

interface MultiSelectDateItemProps {
  option: { value: string; label: string };
  filterValue?: DateFilterValue;
  onDateChange: (start?: string, end?: string) => void;
  onClear: () => void;
}

type DynamicDateFilterProps = SingleSelectProps | MultiSelectProps;

const DATE_DISPLAY_FORMAT = 'MM/dd/yyyy';
const DATE_API_FORMAT = 'yyyy-MM-dd';

/**
 * The error for a range whose start falls after its end.
 * @param {Date} [fromDate] The range's start.
 * @param {Date} [toDate] The range's end.
 * @return {string | null} The error, or null when the range is valid.
 */
function validateDateRange(fromDate?: Date, toDate?: Date): string | null {
  if (fromDate && toDate && isAfter(fromDate, toDate)) {
    return 'Start date must be before end date';
  }

  return null;
}

/**
 * One date type at a time, chosen by radio, with its From/To range.
 * @param {SingleSelectProps} props The filter.
 * @return {JSX.Element} The filter.
 */
function SingleSelectDateFilter({
  value,
  onChange,
  dateOptions,
  headingText,
}: SingleSelectProps) {
  const startParsed = parseDateString(value?.start);
  const endParsed = parseDateString(value?.end);
  const startDate = startParsed ? format(startParsed, DATE_DISPLAY_FORMAT) : '';
  const endDate = endParsed ? format(endParsed, DATE_DISPLAY_FORMAT) : '';
  const selectedType = value?.type || 'none';

  const [validationError, setValidationError] = useState<string | null>(null);

  const handleTypeChange = useCallback(
    (selectedValue: string | number | undefined) => {
      const value = selectedValue as string;
      if (value === 'none') {
        setValidationError(null);
        onChange(undefined);
      } else {
        if (startParsed && endParsed) {
          const error = validateDateRange(startParsed, endParsed);
          if (!error) {
            onChange({
              type: value,
              start: format(startParsed, DATE_API_FORMAT),
              end: format(endParsed, DATE_API_FORMAT),
            });
          }
        } else {
          onChange({
            type: value,
            start: undefined,
            end: undefined,
          });
        }
      }
    },
    [startParsed, endParsed, onChange]
  );

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      if (selectedType === 'none') return;

      const error = validateDateRange(
        date ?? undefined,
        endParsed ?? undefined
      );
      setValidationError(error);

      if (!error && (date || endParsed)) {
        onChange({
          type: selectedType,
          start: date ? format(date, DATE_API_FORMAT) : undefined,
          end: endParsed ? format(endParsed, DATE_API_FORMAT) : undefined,
        });
      }
    },
    [selectedType, endParsed, onChange]
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      if (selectedType === 'none') return;

      const error = validateDateRange(
        startParsed ?? undefined,
        date ?? undefined
      );
      setValidationError(error);

      if (!error && (startParsed || date)) {
        onChange({
          type: selectedType,
          start: startParsed ? format(startParsed, DATE_API_FORMAT) : undefined,
          end: date ? format(date, DATE_API_FORMAT) : undefined,
        });
      }
    },
    [selectedType, startParsed, onChange]
  );

  const isDisabled = selectedType === 'none';

  return (
    <div className={styles.container}>
      <RadioButtonGroup
        legendText={headingText}
        name="date-filter-type"
        valueSelected={selectedType}
        onChange={handleTypeChange}
        orientation="vertical"
        className={styles.radioGroup}
      >
        <RadioButton labelText="None" value="none" id="date-type-none" />
        {dateOptions.map((option) => (
          <RadioButton
            key={option.value}
            labelText={option.label}
            value={option.value}
            id={`date-type-${option.value}`}
          />
        ))}
      </RadioButtonGroup>

      <div className={styles.dateInputs}>
        <DatePicker
          datePickerType="single"
          dateFormat="m/d/Y"
          onChange={(dates) => handleStartDateChange(dates[0])}
          value={startDate}
        >
          <DatePickerInput
            id="start-date"
            placeholder="mm/dd/yyyy"
            labelText="From"
            size="md"
            invalid={!!validationError}
            disabled={isDisabled}
          />
        </DatePicker>

        <DatePicker
          datePickerType="single"
          dateFormat="m/d/Y"
          onChange={(dates) => handleEndDateChange(dates[0])}
          value={endDate}
        >
          <DatePickerInput
            id="end-date"
            placeholder="mm/dd/yyyy"
            labelText="To"
            size="md"
            invalid={!!validationError}
            invalidText={validationError || undefined}
            disabled={isDisabled}
          />
        </DatePicker>
      </div>
    </div>
  );
}

export interface DateRangeFieldProps {
  /** Namespaces the pickers (`start-date-<id>`, `end-date-<id>`). */
  id: string;
  label: ReactNode;
  /** The Clear button's accessible name; omitted, it reads "Clear". */
  clearLabel?: string;
  /** Range bounds as yyyy-MM-dd. */
  start?: string;
  end?: string;
  onChange: (start?: string, end?: string) => void;
  onClear: () => void;
  size?: 'sm' | 'md';
  /** Exposes the range as a group named by its label. */
  labelledGroup?: boolean;
}

/**
 * One date range: its label, From and To pickers side by side, and a Clear for
 * this range alone, refusing a From after its To.
 * @param {DateRangeFieldProps} props The range.
 * @return {JSX.Element} The range.
 */
export function DateRangeField({
  id,
  label,
  clearLabel,
  start,
  end,
  onChange,
  onClear,
  size = 'md',
  labelledGroup = false,
}: DateRangeFieldProps) {
  const labelId = useId();
  const startParsed = parseDateString(start);
  const endParsed = parseDateString(end);
  const startDate = startParsed ? format(startParsed, DATE_DISPLAY_FORMAT) : '';
  const endDate = endParsed ? format(endParsed, DATE_DISPLAY_FORMAT) : '';
  const hasValue = !!(start || end);

  const [validationError, setValidationError] = useState<string | null>(null);

  const handleStartDateChange = useCallback(
    (date: Date | null) => {
      const error = validateDateRange(
        date ?? undefined,
        endParsed ?? undefined
      );
      setValidationError(error);

      if (!error) {
        onChange(
          date ? format(date, DATE_API_FORMAT) : undefined,
          endParsed ? format(endParsed, DATE_API_FORMAT) : undefined
        );
      }
    },
    [endParsed, onChange]
  );

  const handleEndDateChange = useCallback(
    (date: Date | null) => {
      const error = validateDateRange(
        startParsed ?? undefined,
        date ?? undefined
      );
      setValidationError(error);

      if (!error) {
        onChange(
          startParsed ? format(startParsed, DATE_API_FORMAT) : undefined,
          date ? format(date, DATE_API_FORMAT) : undefined
        );
      }
    },
    [startParsed, onChange]
  );

  const handleClear = useCallback(() => {
    setValidationError(null);
    onClear();
  }, [onClear]);

  return (
    <div
      className={styles.multiSelectItem}
      {...(labelledGroup && { 'role': 'group', 'aria-labelledby': labelId })}
    >
      <div className={styles.multiSelectLabel} id={labelId}>
        {label}
      </div>
      <div className={styles.dateInputs}>
        <DatePicker
          datePickerType="single"
          dateFormat="m/d/Y"
          onChange={(dates) => handleStartDateChange(dates[0])}
          value={startDate}
        >
          <DatePickerInput
            id={`start-date-${id}`}
            placeholder="mm/dd/yyyy"
            labelText="From"
            size={size}
            invalid={!!validationError}
          />
        </DatePicker>

        <DatePicker
          datePickerType="single"
          dateFormat="m/d/Y"
          onChange={(dates) => handleEndDateChange(dates[0])}
          value={endDate}
        >
          <DatePickerInput
            id={`end-date-${id}`}
            placeholder="mm/dd/yyyy"
            labelText="To"
            size={size}
            invalid={!!validationError}
            invalidText={validationError || undefined}
          />
        </DatePicker>
      </div>
      <Button
        kind="tertiary"
        size="sm"
        onClick={handleClear}
        className={styles.clearButton}
        disabled={!hasValue}
        aria-label={clearLabel}
      >
        Clear
      </Button>
    </div>
  );
}

/**
 * One option's range in the multi-select date filter.
 * @param {MultiSelectDateItemProps} props The option's range.
 * @return {JSX.Element} The range.
 */
function MultiSelectDateItem({
  option,
  filterValue,
  onDateChange,
  onClear,
}: MultiSelectDateItemProps) {
  return (
    <DateRangeField
      id={option.value}
      label={option.label}
      start={filterValue?.start}
      end={filterValue?.end}
      onChange={onDateChange}
      onClear={onClear}
    />
  );
}

/**
 * A From/To range per date type, any number set at once.
 * @param {MultiSelectProps} props The filter.
 * @return {JSX.Element} The filter.
 */
function MultiSelectDateFilter({
  values,
  onChange,
  dateOptions,
  headingText,
}: MultiSelectProps) {
  /**
   * The value set for a date type.
   * @param {string} type The date type.
   * @return {DateFilterValue | undefined} Its value, if set.
   */
  function getFilterValue(type: string): DateFilterValue | undefined {
    return values?.find((v) => v.type === type);
  }

  const handleDateChange = useCallback(
    (type: string, start?: string, end?: string) => {
      if (!start && !end) {
        const newValues = (values ?? []).filter((v) => v.type !== type);
        onChange(newValues.length > 0 ? newValues : undefined);
        return;
      }

      const existingIndex = (values ?? []).findIndex((v) => v.type === type);
      if (existingIndex >= 0) {
        const newValues = (values ?? []).map((v) => {
          return v.type === type ? { ...v, start, end } : v;
        });
        onChange(newValues);
      } else {
        const newValues = [...(values ?? []), { type, start, end }];
        onChange(newValues);
      }
    },
    [values, onChange]
  );

  const handleClear = useCallback(
    (type: string) => {
      const newValues = (values ?? []).filter((v) => v.type !== type);
      onChange(newValues.length > 0 ? newValues : undefined);
    },
    [values, onChange]
  );

  return (
    <div className={styles.container}>
      {headingText && <div className={styles.headingText}>{headingText}</div>}
      <div className={styles.multiSelectContainer}>
        {dateOptions.map((option) => (
          <MultiSelectDateItem
            key={option.value}
            option={option}
            filterValue={getFilterValue(option.value)}
            onDateChange={(start, end) =>
              handleDateChange(option.value, start, end)
            }
            onClear={() => handleClear(option.value)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * A date filter: one date type at a time (single-select) or a range per type
 * (multi-select).
 * @param {DynamicDateFilterProps} props The filter.
 * @return {JSX.Element} The filter.
 */
export function DynamicDateFilter(props: DynamicDateFilterProps) {
  if (props.variant === 'multi-select') {
    return <MultiSelectDateFilter {...props} />;
  }

  return <SingleSelectDateFilter {...props} />;
}
